import React, { useEffect, useState } from 'react';
import api from '../api';
import Modal from '../Modal';
import { Badge } from '../ui';
import { useLang } from '../Layout';

const T = {
  bn: { title: 'বেতন', sub: 'সাপ্তাহিক মজুরি হিসাব ও পরিশোধ', compute: 'বেতন হিসাব করুন', computing: 'হিসাব হচ্ছে…', weekLabel: 'সপ্তাহ (শনি থেকে শুরু)', raw: 'মোট (raw)', net: 'দেয় (net)', paid: 'পরিশোধিত', days: 'দিন', perDay: '/দিন', prevBal: 'আগের সপ্তাহের বাকি:', payBtn: 'টাকা দিন', empty: 'এই সপ্তাহে বেতন হিসাব হয়নি। "বেতন হিসাব করুন" চাপুন।', payTitle: 'বেতন দিন', paidLabel: 'আগে দিয়েছেন:', payAmt: 'এবার কত দেবেন (৳)', note: 'নোট', notePh: 'ঐচ্ছিক', confirm: '✅ পরিশোধ করুন' },
  en: { title: 'Salaries', sub: 'Weekly wage calculation & payment', compute: 'Compute Salaries', computing: 'Computing…', weekLabel: 'Week (starts Saturday)', raw: 'Total (raw)', net: 'Net', paid: 'Paid', days: 'days', perDay: '/day', prevBal: 'Balance forward:', payBtn: 'Pay', empty: 'No salaries computed this week. Click "Compute Salaries".', payTitle: 'Pay Salary', paidLabel: 'Previously paid:', payAmt: 'Amount to pay (৳)', note: 'Note', notePh: 'Optional', confirm: '✅ Confirm Payment' },
};

// BD week helper: most recent Saturday
function weekStartFor(dateStr) {
  const [y, m, d] = dateStr.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  const diff = (dt.getUTCDay() + 1) % 7;
  dt.setUTCDate(dt.getUTCDate() - diff);
  return `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, '0')}-${String(dt.getUTCDate()).padStart(2, '0')}`;
}

export default function Salaries() {
  const { lang } = useLang(); const t = T[lang];
  const [week, setWeek] = useState(() => weekStartFor(new Date().toISOString().slice(0, 10)));
  const [list, setList] = useState([]);
  const [totals, setTotals] = useState(null);
  const [busy, setBusy] = useState(false);
  const [payFor, setPayFor] = useState(null);
  const [payAmount, setPayAmount] = useState('');
  const [payNote, setPayNote] = useState('');

  const load = () => api.get(`/owner/salaries?week_start=${week}`).then(({ data }) => { setList(data.salaries); setTotals(data.totals); }).catch(() => {});
  useEffect(() => { load(); }, [week]);

  async function compute() {
    setBusy(true);
    await api.post('/owner/salaries/compute', { week_start: week });
    setBusy(false);
    load();
  }

  async function pay(e) {
    e.preventDefault();
    const amount = payAmount === '' ? undefined : Number(payAmount);
    await api.post(`/owner/salaries/${payFor.id}/pay`, { amount, note: payNote });
    setPayFor(null); setPayAmount(''); setPayNote('');
    load();
  }

  const fmt = (n) => '৳' + Number(n || 0).toLocaleString();

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold text-[#0F172A]">{t.title}</h1>
          <p className="text-sm text-[#475569]">{t.sub}</p>
        </div>
        <button onClick={compute} disabled={busy} className="btn text-sm bg-[#F59E0B] hover:bg-[#D4565D] text-white">{busy ? t.computing : t.compute}</button>
      </div>

      <div>
        <label className="label">{t.weekLabel}</label>
        <input type="date" className="input" value={week} onChange={(e) => setWeek(e.target.value)} />
      </div>

      {totals && (
        <div className="flex gap-3">
          <div className="flex-1 bg-white rounded-xl shadow-[0_1px_3px_rgba(0,0,0,0.08)] p-3 text-center">
            <div className="text-lg font-bold text-[#0F172A]">{fmt(totals.raw)}</div>
            <div className="text-xs text-[#475569]">{t.raw}</div>
          </div>
          <div className="flex-1 bg-white rounded-xl shadow-[0_1px_3px_rgba(0,0,0,0.08)] p-3 text-center border-t-2 border-[#F59E0B]">
            <div className="text-lg font-bold text-[#F59E0B]">{fmt(totals.net)}</div>
            <div className="text-xs text-[#475569]">{t.net}</div>
          </div>
          <div className="flex-1 bg-white rounded-xl shadow-[0_1px_3px_rgba(0,0,0,0.08)] p-3 text-center">
            <div className="text-lg font-bold text-[#0F172A]">{fmt(totals.paid)}</div>
            <div className="text-xs text-[#475569]">{t.paid}</div>
          </div>
        </div>
      )}

      {/* Charm table */}
      <div className="bg-white rounded-xl shadow-[0_1px_3px_rgba(0,0,0,0.08)] overflow-hidden">
        <table className="charm-table">
          <thead><tr><th>Worker</th><th>Contractor</th><th>Days</th><th>Rate</th><th className="text-right">Net</th><th className="text-right">Paid</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {list.map((s) => (
              <tr key={s.id}>
                <td><div><span className="font-medium text-[#0F172A]">{s.worker_name}</span>{s.balance_forward > 0 && <div className="text-xs text-[#F59E0B]">{t.prevBal} {fmt(s.balance_forward)}</div>}</div></td>
                <td className="text-[#475569]">{s.contractor_name}</td>
                <td className="text-[#475569]">{s.days_worked} {t.days}</td>
                <td className="text-[#475569]">{fmt(s.rate_per_day)}{t.perDay}</td>
                <td className="text-right font-bold text-[#0F172A]">{fmt(s.net_amount)}</td>
                <td className="text-right text-[#475569]">{fmt(s.amount_paid)}</td>
                <td><Badge color={s.status}>{s.status}</Badge></td>
                <td>
                  {s.status !== 'paid' && (
                    <button onClick={() => { setPayFor(s); setPayAmount(String(s.net_amount - s.amount_paid)); }}
                      className="px-3 py-1 text-xs bg-[#F59E0B] hover:bg-[#D4565D] text-white rounded-lg transition">{t.payBtn}</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {list.length === 0 && <div className="text-center py-12 text-[#94A3B8] text-sm">{t.empty}</div>}
      </div>

      <Modal open={!!payFor} onClose={() => setPayFor(null)} title={`${t.payTitle} — ${payFor?.worker_name || ''}`}>
        <form onSubmit={pay} className="space-y-4">
          <div className="bg-[#FFFBEB] rounded-xl p-4 text-center">
            <div className="text-xs text-[#475569]">{t.net}</div>
            <div className="text-2xl font-bold text-[#F59E0B]">{fmt(payFor?.net_amount)}</div>
            <div className="text-xs text-[#475569] mt-1">{t.paidLabel} {fmt(payFor?.amount_paid)}</div>
          </div>
          <div>
            <label className="label">{t.payAmt}</label>
            <input className="input text-lg font-bold" type="number" value={payAmount} onChange={(e) => setPayAmount(e.target.value)} />
          </div>
          <div>
            <label className="label">{t.note}</label>
            <input className="input" value={payNote} onChange={(e) => setPayNote(e.target.value)} placeholder={t.notePh} />
          </div>
          <button className="btn-primary w-full py-3">{t.confirm}</button>
        </form>
      </Modal>
    </div>
  );
}
